import { tracking } from '@dazn/bootstrap-lib';

import {
    hasSecondLevelTracking,
    shouldTrackProp,
} from './hasSecondLevelTracking';
import { get, set } from './props';

const isObject = (thing: unknown): thing is object =>
    Boolean(thing) && typeof thing === 'object';

export type Getter<G> = () => G;
export interface Optionals<S> {
    setter?: (value: S) => void;
    shouldNotManipulate?: boolean;
    replacedWith?: string;
}
export interface Globals {
    window: Window & typeof globalThis;
    trackUsage: null | tracking.TrackUsage;
    logger: (message: string) => void;
}
export type CreateOneAccessorProps<G, S> = Globals &
    Optionals<S> & {
        getter: Getter<G>;
        propPath: string;
    };

export const setAccessor = <G, S>({
    window,
    trackUsage,
    logger,
    getter,
    propPath,
    replacedWith,
    setter,
    shouldNotManipulate = false,
}: CreateOneAccessorProps<G, S>) => {
    const windowDAZNPath = `window.dazn.${propPath}`;
    const propPathArray = propPath.split('.');
    const nestedPropName = propPathArray[propPathArray.length - 1];

    if (propPathArray.length === 0) {
        throw new Error('Propery should have at least one level');
    }

    if (!get(window.dazn, propPathArray)) {
        set(window.dazn, propPathArray, {});
    }

    const warnIfDeprecated = (fullPropName: string) => {
        if (!replacedWith) {
            return;
        }
        logger(
            [
                `DEPRECATION NOTICE:`,
                `${fullPropName} is deprecated and will be removed.`,
                `Please use ${replacedWith}`,
            ].join(' '),
        );
    };

    const proxied: Record<string, unknown> = {};
    const deepProxy = (obj: any, propertyName: string) => {
        for (const key in obj) {
            const newKey = `${propertyName}.${key}`;
            if (isObject(obj[key]) && proxied[newKey] === undefined) {
                if (hasSecondLevelTracking(propertyName, key)) {
                    obj[key] = new Proxy(obj[key], {
                        get: function (
                            obj: Record<string, unknown>,
                            prop: string,
                        ): unknown {
                            trackUsage?.({
                                functionName: [propPath, key, prop].join('.'),
                            });
                            return obj[prop];
                        },
                    });
                }
                proxied[newKey] = obj[key];
            }
        }
        return new Proxy(obj, {
            get: function (
                obj: Record<string, unknown>,
                prop: string,
            ): unknown {
                if (!shouldTrackProp(`${propertyName}.${prop}`)) {
                    trackUsage?.({
                        functionName: [propPath, prop].join('.'),
                    });
                }
                return obj[prop];
            },
        });
    };

    const propertyObject =
        get(window.dazn, propPathArray.slice(0, -1)) ?? window.dazn;
    Object.defineProperty(propertyObject, nestedPropName, {
        enumerable: true,
        get() {
            warnIfDeprecated(windowDAZNPath);
            const getterAPI = getter();
            if (trackUsage) {
                if (
                    isObject(getterAPI) &&
                    proxied[nestedPropName] !== undefined &&
                    Proxy !== undefined
                ) {
                    proxied[nestedPropName] = deepProxy(
                        getterAPI,
                        nestedPropName,
                    );
                    return proxied[nestedPropName];
                } else {
                    trackUsage({
                        functionName: propPath,
                    });
                }
            }
            return getterAPI;
        },
        set: value => {
            warnIfDeprecated(windowDAZNPath);
            trackUsage?.({
                functionName: propPath,
            });
            if (setter === undefined || shouldNotManipulate) {
                logger(`You can not manipulate ${windowDAZNPath} directly`);
            }
            return setter?.(value);
        },
    });
};
